import { ChangeDetectorRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ThemeService } from '@sb/shared/theme';
import { skip } from 'rxjs';

/**
 * Root-scoped on purpose: `toObservable` is backed by an effect, and an effect created in
 * a component's injection context is a view effect, which never runs in the detached views
 * this exists for.
 */
@Injectable({ providedIn: 'root' })
class ThemeModeChanges {
  mode$ = toObservable(inject(ThemeService).mode);
}

/**
 * Repaints the calling view whenever the theme mode changes.
 *
 * Colours derived from a user-picked colour are solved in JS against the active mode
 * (`userColorBackground`, the `userColorBg` pipe), so they only re-solve while change
 * detection visits the view. Views under a `ChangeDetectorRef.detach()` — parts of the
 * schedule detach for performance — are never visited and keep the previous theme's
 * colours until something else repaints them.
 *
 * Must be called from an injection context (field initializer or constructor). It binds
 * to the *calling* view's change detector: a component's own host bindings are evaluated
 * by its parent view, so a host-bound colour has to be repainted from the component that
 * declares the element, not from the component that owns the binding.
 */
export function useRepaintOnThemeChange(): void {
  const changeDetectorRef = inject(ChangeDetectorRef);
  const modeChanges = inject(ThemeModeChanges).mode$;

  // The current mode replays to every subscriber. Repainting on it is both pointless — the
  // first render already used it — and illegal, because subscribing happens while the
  // calling view is still in creation mode.
  modeChanges.pipe(skip(1), takeUntilDestroyed()).subscribe(() => changeDetectorRef.detectChanges());
}
